import { t } from '@lingui/macro';
import type { MessagingRoomSummary } from '@jema-manager/shared/types/messaging.types';

const normalizeSpecialtyKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const translateKnownLeakedLabel = (value: string) => {
  const normalized = value.trim();

  switch (normalized) {
    case 'shared.layout.AppShell.role-admin':
    case 'diseño.compartido.AppShell.role-admin':
    case 'diseño.compartido.AppShell.requester-role-admin':
      return t({ id: 'features.messages.counterparty.admin', message: 'Admin' });
    case 'shared.layout.AppShell.role-super-admin':
    case 'diseño.compartido.AppShell.role-super-admin':
    case 'diseño.compartido.AppShell.requester-role-super-admin':
      return t({ id: 'features.messages.counterparty.superadmin', message: 'Superadmin' });
    default:
      return normalized;
  }
};

const formatSpecialtyForMessaging = (specialty?: string | null) => {
  if (!specialty) return null;

  switch (normalizeSpecialtyKey(specialty)) {
    case 'vascular':
      return t({ id: 'features.messages.specialty.vascular', message: 'Vascular' });
    case 'dentistry':
      return t({ id: 'features.messages.specialty.dentistry', message: 'Dentistry' });
    case 'optical':
    case 'optics':
      return t({ id: 'features.messages.specialty.optical', message: 'Optical' });
    case 'cardiology':
      return t({ id: 'features.messages.specialty.cardiology', message: 'Cardiology' });
    case 'urology':
      return t({ id: 'features.messages.specialty.urology', message: 'Urology' });
    default:
      return specialty
        .split(/[_\s-]+/g)
        .filter(Boolean)
        .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');
  }
};

export const readRoomContextString = (room: MessagingRoomSummary, keys: string[]) => {
  const context =
    room.context && typeof room.context === 'object'
      ? (room.context as Record<string, unknown>)
      : null;

  if (!context) return null;

  for (const key of keys) {
    const value = context[key];
    if (typeof value === 'string' && value.trim()) {
      return translateKnownLeakedLabel(value);
    }
  }

  return null;
};

export const getPatientName = (room: MessagingRoomSummary) =>
  readRoomContextString(room, [
    'patientName',
    'patient_name',
    'patientFullName',
    'patient_full_name',
  ]) ??
  room.patientId ??
  t({ id: 'features.messages.patientFallback', message: 'Patient' });

export const getDoctorName = (room: MessagingRoomSummary) =>
  readRoomContextString(room, [
    'doctorName',
    'doctor_name',
    'doctorFullName',
    'doctor_full_name',
  ]) ??
  room.doctorId ??
  t({ id: 'features.messages.doctorFallback', message: 'Doctor' });

export const getTargetUserName = (room: MessagingRoomSummary) =>
  readRoomContextString(room, [
    'targetUserName',
    'target_user_name',
    'targetName',
    'target_name',
  ]) ??
  room.targetUserId ??
  t({ id: 'features.messages.targetUserFallback', message: 'User' });

export const getTargetUserRole = (room: MessagingRoomSummary) =>
  readRoomContextString(room, ['targetUserRole', 'target_user_role', 'targetRole', 'target_role']);

export const getQuoteName = (room: MessagingRoomSummary) =>
  readRoomContextString(room, [
    'quoteName',
    'quote_name',
    'serviceRequestTitle',
    'service_request_title',
  ]) ??
  room.quoteId ??
  room.roomType.replace(/_/g, ' ');

export const getSpecialtyLabel = (room: MessagingRoomSummary) =>
  formatSpecialtyForMessaging(readRoomContextString(room, ['specialty']) ?? room.specialty);

export const getUnreadCount = (room: MessagingRoomSummary) =>
  Math.max(0, room.unreadCountCache ?? 0);

export const getUnreadMessagePreviews = (room: MessagingRoomSummary) =>
  (room.unreadMessagePreviews ?? [])
    .filter((preview): preview is string => typeof preview === 'string' && Boolean(preview.trim()))
    .slice(-3);

export const getUnreadLabel = (count: number) =>
  count === 1
    ? t({ id: 'features.messages.unread.single', message: '1 unread message' })
    : `${count} ${t({ id: 'features.messages.unread.multiple', message: 'unread messages' })}`;

const getTimestampMs = (value?: string | null) => {
  if (!value) return 0;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const getRoomActivityTimestamp = (room: MessagingRoomSummary) =>
  Math.max(
    getTimestampMs(room.lastActivityAt),
    getTimestampMs(room.lastMessageAt),
    getTimestampMs(room.updatedAt),
    getTimestampMs(room.createdAt)
  );

export const compareRoomsUnreadFirst = (
  firstRoom: MessagingRoomSummary,
  secondRoom: MessagingRoomSummary
) => {
  const firstUnread = getUnreadCount(firstRoom) > 0 ? 1 : 0;
  const secondUnread = getUnreadCount(secondRoom) > 0 ? 1 : 0;

  if (firstUnread !== secondUnread) {
    return secondUnread - firstUnread;
  }

  return getRoomActivityTimestamp(secondRoom) - getRoomActivityTimestamp(firstRoom);
};

export const sortRoomsUnreadFirst = (rooms: MessagingRoomSummary[]) =>
  [...rooms].sort(compareRoomsUnreadFirst);

export const getRoomKindLabel = (room: MessagingRoomSummary) => {
  switch (room.roomType) {
    case 'quote_patient_doctor':
      return t({ id: 'features.messages.roomKind.doctorPatient', message: 'Doctor-patient' });
    case 'admin_patient_direct':
      return t({ id: 'features.messages.roomKind.adminPatient', message: 'Admin-patient' });
    case 'admin_doctor_direct':
      return t({ id: 'features.messages.roomKind.adminDoctor', message: 'Admin-doctor' });
    case 'superadmin_direct':
      return room.doctorId
        ? t({ id: 'features.messages.roomKind.superadminDoctor', message: 'Superadmin-doctor' })
        : t({ id: 'features.messages.roomKind.superadminPatient', message: 'Superadmin-patient' });
    case 'staff_user_direct':
      return t({ id: 'features.messages.roomKind.staffUser', message: 'Admin-user' });
    default:
      return String(room.roomType).replace(/_/g, ' ');
  }
};

export const getRoleBadgeLabel = (role?: string | null) => {
  switch (role) {
    case 'patient':
      return t({ id: 'features.messages.roleBadge.patient', message: 'Patient' });
    case 'doctor':
      return t({ id: 'features.messages.roleBadge.doctor', message: 'Doctor' });
    case 'org_owner':
      return t({ id: 'features.messages.roleBadge.orgOwner', message: 'Org owner' });
    case 'admin':
      return t({ id: 'features.messages.roleBadge.admin', message: 'Admin' });
    case 'super_admin':
      return t({ id: 'features.messages.roleBadge.superadmin', message: 'Superadmin' });
    default:
      return null;
  }
};

export const isDirectMessagingRoom = (room: MessagingRoomSummary) =>
  room.roomType === 'admin_patient_direct' ||
  room.roomType === 'admin_doctor_direct' ||
  room.roomType === 'superadmin_direct' ||
  room.roomType === 'staff_user_direct';

export const getAdminRoomTitle = (room: MessagingRoomSummary) => {
  if (room.roomType === 'staff_user_direct') {
    return getTargetUserName(room);
  }

  if (room.roomType === 'admin_patient_direct') {
    return getPatientName(room);
  }

  if (room.roomType === 'admin_doctor_direct') {
    return getDoctorName(room);
  }

  if (room.roomType === 'superadmin_direct') {
    return room.doctorId ? getDoctorName(room) : getPatientName(room);
  }

  return getQuoteName(room);
};

export const getAdminRoomRoleLabel = (room: MessagingRoomSummary) => {
  if (room.roomType === 'staff_user_direct') {
    return getRoleBadgeLabel(getTargetUserRole(room));
  }

  if (room.roomType === 'admin_patient_direct') {
    return getRoleBadgeLabel('patient');
  }

  if (room.roomType === 'admin_doctor_direct') {
    return getRoleBadgeLabel('doctor');
  }

  if (room.roomType === 'superadmin_direct') {
    return getRoleBadgeLabel(room.doctorId ? 'doctor' : 'patient');
  }

  return null;
};

export const getRoomListTitle = (room: MessagingRoomSummary, role: string) => {
  if (role === 'doctor') {
    if (room.roomType === 'admin_doctor_direct') {
      return t({ id: 'features.messages.counterparty.admin', message: 'Admin' });
    }
    if (room.roomType === 'staff_user_direct') {
      return t({ id: 'features.messages.counterparty.admin', message: 'Admin' });
    }
    if (room.roomType === 'superadmin_direct' && room.doctorId) {
      return t({ id: 'features.messages.counterparty.superadmin', message: 'Superadmin' });
    }
    return getPatientName(room);
  }

  if (role === 'patient') {
    if (room.roomType === 'admin_patient_direct') {
      return t({ id: 'features.messages.counterparty.admin', message: 'Admin' });
    }
    if (room.roomType === 'staff_user_direct') {
      return t({ id: 'features.messages.counterparty.admin', message: 'Admin' });
    }
    if (room.roomType === 'superadmin_direct') {
      return t({ id: 'features.messages.counterparty.superadmin', message: 'Superadmin' });
    }
    return getDoctorName(room);
  }

  return getQuoteName(room);
};

export const getRoomConversationTitle = (room: MessagingRoomSummary, role: string) => {
  if (role === 'doctor' || role === 'patient') {
    return getRoomListTitle(room, role);
  }

  if (role === 'admin' || role === 'super_admin') {
    return getAdminRoomTitle(room);
  }

  return getQuoteName(room);
};

export const getRoomSearchText = (room: MessagingRoomSummary, role: string) =>
  [
    getRoomListTitle(room, role),
    getRoomConversationTitle(room, role),
    getAdminRoomTitle(room),
    readRoomContextString(room, [
      'patientName',
      'patient_name',
      'patientFullName',
      'patient_full_name',
    ]),
    readRoomContextString(room, [
      'doctorName',
      'doctor_name',
      'doctorFullName',
      'doctor_full_name',
    ]),
    getTargetUserName(room),
    getTargetUserRole(room),
    getAdminRoomRoleLabel(room),
    getRoomKindLabel(room),
    getQuoteName(room),
    getSpecialtyLabel(room),
    room.status,
    room.roomType,
    room.patientId,
    room.doctorId,
    room.targetUserId,
    ...getUnreadMessagePreviews(room),
  ]
    .filter((value): value is string => typeof value === 'string' && Boolean(value.trim()))
    .join(' ')
    .toLowerCase();
